import { useCallback } from 'react';
import pacote from '../../../package.json';
import { buildDownloadState, normalizeDatasetType } from './_syncCommons';
import { OBRIGATORY_DB_TABLES } from '@/constants';
import { useEvents } from '../use-events/useEvents';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { Tables, DataSetsProps, AmbientTypes, ReturnSyncDatasetsProps, listTables } from '@/types';
import { getOnStorage, setOnStorage, TABLE_DISPLAY_LABELS } from '@/shared';
import { removeDatasetsForaDaLista } from '@/shared/bfSeller';
import { useAlert } from '@/hooks/AlertContext';
import { useGlobals } from '@/hooks/GlobalContext';
import { useFetchAxios } from '@/hooks/FetchAxiosContext';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { usePrepareFiles } from './_usePrepareFiles';

type GetLinksParams = {
  codPessoa: string;
  isBackground: boolean;
  datasets?: DataSetsProps[];
};

type GetLinksStates = {
  setError: (error: boolean) => void;
  setNoUpdate: (noUpdate: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  setPreparing: (preparing: boolean) => void;
  setIsOpenedModal: (isOpenedModal: boolean) => void;
  loadFiles: ReturnType<typeof usePrepareFiles>['loadFiles'];
};

const GETDATASET_TIMEOUT_MS = 45000;
let globalSyncInProgress = false;

const validateVersion = async (resp: any) => {
  if (resp?.data?.versao_app?.length && !window.agp_config?.DISABLE_VALIDATE_VERSION) {
    const currentAmbient = window.agp_config?.AMBIENT;
    const isValidVersion = resp?.data?.versao_app?.some((v: { ambiente: AmbientTypes; versao_app: string }) => {
      const isSameAmbient = v.ambiente === currentAmbient;
      if (!isSameAmbient) return false;

      const currentVersion = Number(pacote.version.split('.').join(''));
      const requiredVersion = Number(v.versao_app.split('.').join(''));
      return currentVersion >= requiredVersion;
    });

    if (!isValidVersion) {
      alert('A versão do app está desatualizada. Vamos atualizar o app para você.');
      await unregisterServiceWorker({ preserveLocalStorage: true, setReloadFlag: false });
      window.location.reload();
      return;
    }
  }
};

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = '';
};

function startDownload() {
  window.addEventListener('beforeunload', handleBeforeUnload);
}

function finishDownload() {
  window.removeEventListener('beforeunload', handleBeforeUnload);
}

export const useGetLinks = (_useState: GetLinksStates) => {
  const db = useDbFunctions();
  const { validateTable, getOnDB } = db;
  const { hideAlert, showAlert } = useAlert();
  const { axiosCtx } = useFetchAxios();
  const { registerEvent } = useEvents(db);
  const { setLastSync, getDate } = useGlobals();

  const getLinks = useCallback(
    async ({ isBackground, codPessoa, datasets }: GetLinksParams): Promise<ReturnSyncDatasetsProps> => {
      if (globalSyncInProgress) return { isSuccess: false, isRunning: true, hasUpdate: false, datasets: [] };
      globalSyncInProgress = true;

      try {
        _useState.setPreparing(true);
        _useState.setError(false);
        _useState.setIsSuccess(false);
        _useState.setNoUpdate(false);

        const rcaInfos = getOnStorage('rca_usuario');
        // `||` e não `??`: quem chama monta o código com template string, então "ausente" chega como '' ou 'undefined'.
        const informado = codPessoa && codPessoa !== 'undefined' ? codPessoa : undefined;
        const userCode = informado || getOnStorage('cod_pessoa') || rcaInfos?.codPessoa || rcaInfos?.cod_pessoa;
        if (!userCode) {
          _useState.setError(true);
          return {
            isSuccess: false,
            isRunning: false,
            hasUpdate: false,
            datasets: datasets?.map((dataset) => ({ table: dataset.TIPO_ARQUIVO as Tables, isSynced: false })) || [],
          };
        }

        if (!isBackground) {
          _useState.setIsOpenedModal(true);
          localStorage.setItem('login_bootstrap', 'in-progress');
        }

        const resp = await axiosCtx.get(`/getdataset/${userCode}`, { timeout: GETDATASET_TIMEOUT_MS }).catch((error) => {
          if (!isBackground) localStorage.removeItem('login_bootstrap');
          _useState.setError(true);
          throw new Error(error?.message ?? 'Erro ao baixar arquivos');
        });

        // Valida a versão com base na resposta da API
        await validateVersion(resp);

        const [representante] = resp?.data?.representante || [];

        const listToDownload: DataSetsProps[] = [];
        const list: DataSetsProps[] = datasets || representante?.link_Dataset || [];

        setOnStorage('datasets', list);

        // Tipo que existia e sumiu da lista (combo vencido) sai do banco antes de decidir o que baixar.
        const removidas = await removeDatasetsForaDaLista(
          list.map((dataset) => normalizeDatasetType(dataset.TIPO_ARQUIVO)),
          { preservar: OBRIGATORY_DB_TABLES }
        );
        // O `tables.current` ainda guarda o dado apagado: relê do banco, agora vazio.
        await Promise.all(
          removidas
            .filter((store) => listTables.includes(store as Tables))
            .map((store) => getOnDB(store as Tables, undefined, undefined, true))
        );

        const { temDado, foiBaixado } = await buildDownloadState(validateTable);

        /**
         * Arquivo por arquivo, e não tipo por tipo: um `catalogo_1.json` republicado não faz o
         * `catalogo_0.json`, que continua igual, descer de novo. Quando o tipo não tem conteúdo
         * nenhum (`!temDado`), todos os arquivos dele caem aqui de qualquer forma.
         */
        for (const dataset of list) {
          const tipoArquivo = normalizeDatasetType(dataset.TIPO_ARQUIVO);
          const isFilledOnDB = temDado(tipoArquivo);
          const isRegisteredSuccess = foiBaixado(tipoArquivo, dataset.URL_S3, dataset.DT_UPDATE);

          if (!isFilledOnDB || !isRegisteredSuccess) listToDownload.push({ ...dataset, TIPO_ARQUIVO: tipoArquivo });
        }

        const obrigatoryDatasets = OBRIGATORY_DB_TABLES.map((table) => ({
          isFilledOnDB: temDado(normalizeDatasetType(table)),
          table,
        }));

        const isMissingObrigatoryDatasets = obrigatoryDatasets.filter((table) => !table.isFilledOnDB);

        if (!listToDownload.length && !isMissingObrigatoryDatasets.length && !isBackground) {
          _useState.setNoUpdate(true);
          return { isSuccess: true, isRunning: false, hasUpdate: false, datasets: [] };
        }

        if (!listToDownload.length && isMissingObrigatoryDatasets.length && !isBackground) {
          const labels = isMissingObrigatoryDatasets.map((table) => TABLE_DISPLAY_LABELS[table.table]);
          showAlert({
            title: 'Identificamos inconsistências nos dados baixados.',
            message: `Os seguintes arquivos apresentaram problemas durante a sincronização: <b>${labels.join(', ')}</b>. Tente sincronizar novamente. Caso o problema persista, entre em contato com o suporte.`,
            theme: 'warning',
            bgType: 'primary',
            bordered: true,
            borderLeft: true,
            isList: false,
            time: 1000000000,
            close: 'icon',
          });
          return {
            isSuccess: false,
            isRunning: false,
            hasUpdate: false,
            datasets:
              isMissingObrigatoryDatasets.map((item) => ({ table: item.table as Tables, isSynced: item.isFilledOnDB })) || [],
          };
        }

        // O aviso de "sair da página" só vale para o sync com modal, em que o usuário está
        // esperando por ele. Um sync em background interrompido não corrompe nada — o
        // `writeDatasetFile` marca o arquivo como `pending` antes de gravar e o próximo sync o baixa
        // de novo —, e não pode transformar todo reload do app num "Recarregar site?".
        if (!isBackground) startDownload();
        _useState.setIsLoading(true);
        await _useState.loadFiles(listToDownload, true, isBackground);

        if (!isMissingObrigatoryDatasets.length) hideAlert();

        const formattedDate = getDate();
        setLastSync(formattedDate);
        _useState.setError(false);
        localStorage.setItem('login_bootstrap', 'done');

        return {
          isSuccess: true,
          isRunning: false,
          hasUpdate: !!listToDownload.length,
          datasets: listToDownload.map((dataset) => ({ table: dataset.TIPO_ARQUIVO as Tables, isSynced: true })) || [],
        };
      } catch (error) {
        registerEvent({
          tipoEvento: 'DOWNLOAD',
          obsEvento: 'Erro ao baixar arquivos',
          stEvento: 0,
          offline: true,
        });
        _useState.setError(true);
        return {
          isSuccess: false,
          isRunning: false,
          hasUpdate: false,
          datasets: datasets?.map((dataset) => ({ table: dataset.TIPO_ARQUIVO as Tables, isSynced: false })) || [],
        };
      } finally {
        // No `finally`: se o download lançar, o listener do `beforeunload` ficava registrado até a
        // aba fechar, e todo reload pedia confirmação.
        finishDownload();
        _useState.setIsLoading(false);
        _useState.setPreparing(false);
        globalSyncInProgress = false;
      }
    },
    [getDate, axiosCtx]
  );

  return getLinks;
};
